import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/layout/Layout";
import EmptyState from "../components/EmptyState";
import AuthGuard from "../components/AuthGuard";
import { useCart } from "../contexts/CartContext";

const CartContent = () => {
    const { cart, loading, updateItem, removeItem, clearCart } = useCart();
    const navigate = useNavigate();

    const onQty = async (productId, qty) => {
        try {
            await updateItem(productId, qty);
        } catch {
            toast.error("Could not update quantity");
        }
    };

    if (loading) {
        return (
            <div className="space-y-3" data-testid="cart-loading">
                {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="h-24 skeleton rounded-2xl" />
                ))}
            </div>
        );
    }

    if (!cart || cart.items.length === 0) {
        return (
            <EmptyState
                icon={<ShoppingBag className="h-10 w-10" />}
                title="Your cart is empty"
                description="Discover medicines, vitamins and devices curated for everyday wellness."
                action={<Link to="/products" className="btn-brand">Start shopping</Link>}
            />
        );
    }

    return (
        <div className="grid gap-10 lg:grid-cols-[1fr_360px]" data-testid="cart-content">
            <div className="space-y-3">
                {cart.items.map((it) => (
                    <div
                        key={it.product_id}
                        className="flex items-center gap-4 rounded-2xl border border-zinc-100 bg-white p-3 transition-all hover:border-brand-200"
                        data-testid={`cart-item-${it.product_id}`}
                    >
                        <Link to={`/products/${it.product_id}`} className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-zinc-50">
                            <img src={it.image} alt={it.name} className="h-full w-full object-cover" />
                        </Link>
                        <div className="min-w-0 flex-1">
                            <Link to={`/products/${it.product_id}`} className="line-clamp-1 text-sm font-medium text-zinc-900 hover:text-brand-700">{it.name}</Link>
                            <p className="text-xs text-zinc-500">{it.brand} • {it.pack_size}</p>
                            <p className="mt-1 text-sm font-semibold text-zinc-900">
                                ₹{it.selling_price}
                                {it.mrp > it.selling_price && <span className="ml-2 text-xs text-zinc-400 line-through">₹{it.mrp}</span>}
                            </p>
                            {it.prescription && (
                                <Link
                                    to={`/prescriptions/${it.prescription.id}`}
                                    className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-brand-200 bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-700 hover:bg-brand-100"
                                    data-testid={`cart-rx-badge-${it.product_id}`}
                                >
                                    <ShieldCheck className="h-3 w-3" />
                                    Rx: {it.prescription.doctor_name || "Prescription attached"}
                                </Link>
                            )}
                        </div>
                        <div className="flex items-center gap-1 rounded-full border border-zinc-200 bg-white p-1">
                            <button
                                onClick={() => onQty(it.product_id, it.qty - 1)}
                                className="rounded-full p-1.5 hover:bg-zinc-50"
                                aria-label="Decrement"
                                data-testid={`cart-decrement-${it.product_id}`}
                            >
                                <Minus className="h-3.5 w-3.5" />
                            </button>
                            <span className="min-w-[1.5rem] text-center text-sm font-medium" data-testid={`cart-qty-${it.product_id}`}>{it.qty}</span>
                            <button
                                onClick={() => onQty(it.product_id, it.qty + 1)}
                                className="rounded-full p-1.5 hover:bg-zinc-50"
                                aria-label="Increment"
                                data-testid={`cart-increment-${it.product_id}`}
                            >
                                <Plus className="h-3.5 w-3.5" />
                            </button>
                        </div>
                        <button
                            onClick={() => removeItem(it.product_id)}
                            className="rounded-full p-2 text-zinc-400 hover:bg-zinc-50 hover:text-red-500"
                            aria-label="Remove"
                            data-testid={`cart-remove-${it.product_id}`}
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>
                ))}
                <button
                    onClick={clearCart}
                    className="mt-2 text-xs font-medium text-zinc-500 hover:text-red-500"
                    data-testid="cart-clear-btn"
                >
                    Clear cart
                </button>
            </div>

            <aside className="self-start rounded-3xl border border-zinc-100 bg-white p-6 shadow-soft" data-testid="cart-summary">
                <p className="label-uppercase">Summary</p>
                <h2 className="mt-1 text-xl font-semibold tracking-tight text-zinc-900">Order total</h2>

                <dl className="mt-6 space-y-3 text-sm">
                    <div className="flex justify-between">
                        <dt className="text-zinc-500">Items</dt>
                        <dd className="font-medium" data-testid="summary-items">{cart.total_items}</dd>
                    </div>
                    <div className="flex justify-between">
                        <dt className="text-zinc-500">Subtotal</dt>
                        <dd className="font-medium" data-testid="cart-subtotal">₹{cart.subtotal.toFixed(2)}</dd>
                    </div>
                    {cart.savings > 0 && (
                        <div className="flex justify-between">
                            <dt className="text-zinc-500">You save</dt>
                            <dd className="font-medium text-emerald-600" data-testid="cart-savings">- ₹{cart.savings.toFixed(2)}</dd>
                        </div>
                    )}
                    {/* v3A — Inline free-shipping nudge */}
                    {cart.subtotal < 800 && (
                        <div className="rounded-xl bg-brand-50/70 px-3 py-2 text-xs text-brand-800 dark:bg-brand-900/20 dark:text-brand-200" data-testid="cart-shipping-hint">
                            {cart.subtotal < 499
                                ? <>Add <strong>₹{(499 - cart.subtotal).toFixed(0)}</strong> more to drop shipping to <strong>₹29</strong>, or <strong>₹{(800 - cart.subtotal).toFixed(0)}</strong> more for <strong>FREE shipping</strong>.</>
                                : <>Add <strong>₹{(800 - cart.subtotal).toFixed(0)}</strong> more for <strong>FREE shipping</strong>.</>}
                        </div>
                    )}
                    {cart.subtotal >= 800 && (
                        <div className="rounded-xl bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700" data-testid="cart-free-shipping-earned">
                            🎉 You've earned <strong>FREE shipping</strong>.
                        </div>
                    )}
                    <div className="flex justify-between border-t border-zinc-100 pt-3">
                        <dt className="font-medium text-zinc-900">Estimated total</dt>
                        <dd className="text-lg font-semibold text-zinc-900" data-testid="summary-total">₹{cart.subtotal.toFixed(2)}</dd>
                    </div>
                </dl>

                <button
                    onClick={() => navigate("/checkout")}
                    disabled={cart.items.length === 0}
                    className="btn-brand mt-6 w-full"
                    data-testid="checkout-btn"
                >
                    Proceed to checkout
                    <ArrowRight className="h-4 w-4" />
                </button>
                <p className="mt-3 text-center text-[11px] text-zinc-400">Secure payments by <span className="font-medium text-brand-700">Zoho Payments</span></p>
            </aside>
        </div>
    );
};

const Cart = () => (
    <Layout>
        <section className="mx-auto max-w-7xl px-6 py-10 lg:px-12">
            <header className="mb-8">
                <p className="label-uppercase">Your bag</p>
                <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">Cart</h1>
            </header>
            <AuthGuard>
                <CartContent />
            </AuthGuard>
        </section>
    </Layout>
);

export default Cart;
